import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStudents } from '../../hooks/useStudents';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import StudentForm from '../../components/forms/StudentForm';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const StudentEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { students, loading: listLoading, updateStudent } = useStudents();

  const [student, setStudent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);

  // Resolve student from cache/list
  useEffect(() => {
    // Avoid false "not found" while the list is still loading
    if (listLoading) return;

    const found = students.find((s) => s.id === id);
    if (found) {
      setStudent(found);
      setNotFound(false);
    } else if (students.length > 0) {
      setNotFound(true);
    }
  }, [id, students, listLoading]);

  const handleCancel = () => {
    navigate(`/students/${id}`);
  };

  // Receives university_degree, mqa_recognition, moodle_access, remarks (plus existing fields)
  const handleStudentSubmit = async (formData) => {
    setSaving(true);
    try {
      const result = await updateStudent(id, formData);
      if (!result?.success) {
        alert(result?.error || 'Failed to update student');
        return;
      }
      alert('Student details updated.');
      navigate(`/students/${id}`);
    } catch (e) {
      console.error('Update error:', e);
      alert('An unexpected error occurred while updating the student.');
    } finally {
      setSaving(false);
    }
  };

  if (notFound) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={handleCancel} className="flex items-center">
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">Student Not Found</h1>
        </div>
        <Card>
          <div className="py-8 text-center">
            <p className="text-gray-500">The student you are trying to edit does not exist.</p>
          </div>
        </Card>
      </div>
    );
  }

  if (listLoading || !student) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
{/*          
          <Button variant="outline" onClick={handleCancel} className="flex items-center">
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back
          </Button>
*/}
          <h1 className="text-2xl font-bold text-gray-900">Edit Student</h1>
        </div>
        <Card>
          <div className="py-8 text-center">
            <p className="text-gray-500">Loading student data...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
{/*
        <Button variant="outline" onClick={handleCancel} className="flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          Back
        </Button>
*/}
        <h1 className="text-2xl font-bold text-gray-900">Edit Student</h1>
      </div>

      <Card>
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Update student personal information. Manage subject enrollments from the student page.
          </p>
        </div>

        <StudentForm
          initialData={student}   // includes university_degree, mqa_recognition, moodle_access, remarks
          onSubmit={handleStudentSubmit}
          onCancel={handleCancel}
          loading={saving}
          step={1}                // Student info step
        />
      </Card>
    </div>
  );
};

export default StudentEdit;
